/**
 * Signals tab: how TradeFinder markers look (strip, level emphasis, edge
 * dimming, fade, pulse) and the optional chimes. Browser-local prefs
 * (tradeFinderUxStore).
 */
import React from 'react';
import { useTradeFinderUxStore, STRIP_COUNT_MAX } from '../../store/tradeFinderUxStore';
import { normalizeToBase } from '../../utils/symbolUtils';
import { Switch } from './SignalsTab';
import { ALERT_KINDS, kindName, levelTolFor, tfGlyph, TF_ROOTS, type TfRoot } from './tradeFinderUx';
import { BUY_TONES, SELL_TONES, playChime } from '../../services/tradeFinderSound';
import { EDGE_T_RELIABLE } from '../../store/flowSignalsStore';

const DisplaySection: React.FC<{ symbol: string }> = ({ symbol }) => {
  const ux = useTradeFinderUxStore();
  const base = normalizeToBase(symbol);
  const root: TfRoot = (TF_ROOTS as readonly string[]).includes(base) ? (base as TfRoot) : 'MNQ';
  const tol = levelTolFor(root, ux.levelTol);

  return (
    <div className="tf-display" data-testid="tradefinder-display">
      <div className="levels-group-heading">Display</div>
      <Switch
        on={ux.stripEnabled}
        label="Signal strip"
        description="Latest signals over the chart: side, age, edge t, price, key level. Click one to find it on the chart"
        glyph="▤"
        testId="tradefinder-ux-strip"
        onChange={(on) => ux.set({ stripEnabled: on })}
      />
      <label className="tf-ux-row">
        <span>Signals in the strip</span>
        <input
          type="number"
          min={1}
          max={STRIP_COUNT_MAX}
          step={1}
          value={ux.stripCount}
          onChange={(e) => ux.set({ stripCount: Number(e.target.value) || 1 })}
          data-testid="tradefinder-ux-strip-count"
        />
      </label>
      <Switch
        on={ux.levelEmphasis}
        label="Emphasise markers at key levels"
        description="Slightly larger with a soft glow and the level code (PW, VAH, ONL...) when within the tolerance of a GEX, profile, ON, PD or MOC level"
        glyph="◎"
        testId="tradefinder-ux-emphasis"
        onChange={(on) => ux.set({ levelEmphasis: on })}
      />
      <label className="tf-ux-row">
        <span>Level tolerance, {root} (pts)</span>
        <input
          type="number"
          min={0}
          max={50}
          step={0.25}
          value={tol}
          onChange={(e) => ux.setLevelTol(root, Number(e.target.value))}
          data-testid="tradefinder-ux-level-tol"
        />
      </label>
      <Switch
        on={ux.dimNoEdge}
        label="Dim kinds with a weak measured edge"
        description={`Off by default. When on, a kind whose tuner edge for this symbol is measured and below |t| ${EDGE_T_RELIABLE} draws its disc at 65%. No edge data never dims. The glyph stays crisp`}
        glyph="◐"
        testId="tradefinder-ux-dim"
        onChange={(on) => ux.set({ dimNoEdge: on })}
      />
      <Switch
        on={ux.fade}
        label="Fade older markers"
        description={`Full strength for ${ux.fadeStartMin} min, then the disc fades gently to 70% over the next 50 min. The glyph stays crisp`}
        glyph="◌"
        testId="tradefinder-ux-fade"
        onChange={(on) => ux.set({ fade: on })}
      />
      <label className="tf-ux-row">
        <span>Fade starts after (min)</span>
        <input
          type="number"
          min={1}
          max={60}
          step={1}
          value={ux.fadeStartMin}
          onChange={(e) => ux.set({ fadeStartMin: Number(e.target.value) || 1 })}
          data-testid="tradefinder-ux-fade-start"
        />
      </label>
      <Switch
        on={ux.pulse}
        label="Pulse the newest marker"
        description="A ring pulses around a signal for a few seconds after it fires"
        glyph="◉"
        testId="tradefinder-ux-pulse"
        onChange={(on) => ux.set({ pulse: on })}
      />

      <div className="levels-group-heading">Sound</div>
      <Switch
        on={ux.sound.enabled}
        label="Chimes"
        description="Only while this tab is visible, only for signals that pass the push alert filters (Settings → Alerts). Rising tone = Buy, falling = Sell"
        glyph="♪"
        testId="tradefinder-ux-sound"
        onChange={(on) => ux.setSound({ enabled: on })}
      />
      <label className="tf-ux-row">
        <span>Volume</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={ux.sound.volume}
          onChange={(e) => ux.setSound({ volume: Number(e.target.value) })}
          data-testid="tradefinder-ux-volume"
        />
        <button
          type="button"
          className="tf-ux-test"
          onClick={() => {
            playChime(BUY_TONES, ux.sound.volume);
            setTimeout(() => playChime(SELL_TONES, ux.sound.volume), 450);
          }}
        >
          Test
        </button>
      </label>
      {ALERT_KINDS.map((k) => (
        <Switch
          key={k}
          on={ux.sound.kinds[k]}
          label={`Chime: ${kindName(k)}`}
          glyph={tfGlyph(k)}
          muted={!ux.sound.enabled}
          testId={`tradefinder-ux-sound-${k}`}
          onChange={(on) => ux.setSoundKind(k, on)}
        />
      ))}
    </div>
  );
};

export default DisplaySection;
